import { CheckIcon } from '@icons';
import { Button } from 'features/components/Button';
import { useLanguage } from 'features/hooks/useLanguage';
import { useState } from 'react';
import { LanguageType } from 'types/enums';

interface SurveyDistributionViewProps {
	surveyLink: string;
	pinCode: string;
	onClose: () => void;
}

export const SurveyDistributionView = ({
	surveyLink,
	pinCode,
	onClose
}: SurveyDistributionViewProps) => {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const [copied, setCopied] = useState(false);
	const [copiedPinCode, setCopiedPinCode] = useState(false);

	const handleCopyLink = () => {
		navigator.clipboard
			.writeText(surveyLink)
			.then(() => {
				setCopied(true);
			})
			.catch(err => {
				console.error('Failed to copy text: ', err);
			});
	};

	const handleCopyPinCode = () => {
		navigator.clipboard
			.writeText(pinCode)
			.then(() => {
				setCopiedPinCode(true);
			})
			.catch(err => {
				console.error('Failed to copy pin code: ', err);
			});
	};

	return (
		<div className="flex flex-col gap-6">
			<div className="text-lg font-semibold">{dictionary.instruction}</div>

			<div className="w-full mt-2">
				<div className="text-base font-medium mb-2">{dictionary.step1}</div>
				<div className="p-2 px-3 min-h-[40px] text-sm border bg-gray-300 border-gray-300 rounded-md flex items-center justify-between gap-2">
					<div className="break-all flex-1">{surveyLink}</div>
					{!copied ? (
						<Button
							text={dictionary.copy}
							onClick={handleCopyLink}
							variant="secondary"
						/>
					) : (
						<div className="flex items-center gap-2 text-green-600">
							<CheckIcon />
							<span className="text-sm font-medium">{dictionary.copied}</span>
						</div>
					)}
				</div>
			</div>

			{copied && (
				<div className="w-full mt-2">
					<div className="text-base font-medium">{dictionary.step2}</div>
					<div className="p-2 px-3 min-h-[40px] text-sm border bg-gray-300 border-gray-300 rounded-md flex items-center justify-between gap-2">
						<div className="break-all flex-1">{pinCode}</div>
						{!copiedPinCode ? (
							<Button
								text={dictionary.copy}
								onClick={handleCopyPinCode}
								variant="secondary"
								className="flex-shrink-0"
							/>
						) : (
							<div className="flex items-center gap-2 text-green-600">
								<CheckIcon />
								<span className="text-sm font-medium">{dictionary.copied}</span>
							</div>
						)}
					</div>
				</div>
			)}

			{copied && copiedPinCode && (
				<div className="flex flex-col mt-2">
					<span className="text-base font-medium">{dictionary.step3}</span>
					<div className="flex justify-end mt-2">
						<Button text={dictionary.close} onClick={onClose} variant="primary" />
					</div>
				</div>
			)}
		</div>
	);
};

const DICTIONARY = {
	[LanguageType.English]: {
		instruction: 'Follow the steps below to send the survey to the patient:',
		step1: '1. Copy the survey link and send it to the patient over email.',
		step2: '2. Copy the pin code and send it to the patient over SMS. The pin code is valid for 30 days.',
		step3: 'Click on the status icon to the right of the "Create survey" button to see a summary of all surveys or to find the link and pin code.',
		copy: 'Copy',
		copied: 'Copied!',
		close: 'Close'
	},
	[LanguageType.Norwegian]: {
		instruction: 'Følg trinnene nedenfor for å sende undersøkelsen til pasienten:',
		step1: '1. Kopier undersøkelseslenken og send den til pasienten via e-post.',
		step2: '2. Kopier pin-koden og send den til pasienten via SMS. Pin-koden er gyldig i 30 dager.',
		step3: '3. Klikk på statusikonet til høyre for "Lag undersøkelse" for å se en oversikt over alle undersøkelser eller for å finne lenken og pin-koden.',
		copy: 'Kopier',
		copied: 'Kopiert!',
		close: 'Lukk'
	}
};
